import { useState } from "react";
import {
  View,
  Text,
  ScrollView,
  TouchableOpacity,
  StyleSheet,
  Platform,
  TextInput,
  ActivityIndicator,
  Image,
} from "react-native";
import { useRouter } from "expo-router";
import { Ionicons } from "@expo/vector-icons";
import Head from "expo-router/head";
import { PublicNav } from "../../components/PublicNav";

const NAVY = "#1B2A4A";
const NAVY_DARK = "#111C30";
const ORANGE = "#F5A623";
const WHITE = "#FFFFFF";
const TEXT = "#1F2937";

export default function SupportScreen() {
  const router = useRouter();

  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [message, setMessage] = useState("");
  const [images, setImages] = useState<string[]>([]); // base64 data URIs, max 3
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(false);
  const [error, setError] = useState("");

  // Resize an uploaded image to max 1200px on its longest edge and return a base64 JPEG.
  // Keeps the payload reasonable so 3 large screenshots don't blow out the request body.
  function resizeImageWeb(file: File, maxSize: number): Promise<string> {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const img = new window.Image();
        img.onload = () => {
          const canvas = document.createElement("canvas");
          let w = img.width;
          let h = img.height;
          if (Math.max(w, h) > maxSize) {
            if (w > h) { h = (h / w) * maxSize; w = maxSize; }
            else { w = (w / h) * maxSize; h = maxSize; }
          }
          canvas.width = w;
          canvas.height = h;
          canvas.getContext("2d")!.drawImage(img, 0, 0, w, h);
          resolve(canvas.toDataURL("image/jpeg", 0.82));
        };
        img.onerror = reject;
        img.src = e.target?.result as string;
      };
      reader.onerror = reject;
      reader.readAsDataURL(file);
    });
  }

  function handlePickImages() {
    if (Platform.OS !== "web") return;
    const input = document.createElement("input");
    input.type = "file";
    input.accept = "image/*";
    input.multiple = true;
    input.onchange = async (e: any) => {
      const files: File[] = Array.from(e.target.files || []);
      if (files.length === 0) return;
      const remaining = 3 - images.length;
      if (remaining <= 0) {
        setError("Max 3 images per message");
        return;
      }
      const toProcess = files.slice(0, remaining);
      const resized = await Promise.all(toProcess.map((f) => resizeImageWeb(f, 1200)));
      setImages((prev) => [...prev, ...resized].slice(0, 3));
    };
    input.click();
  }

  function removeImage(idx: number) {
    setImages((prev) => prev.filter((_, i) => i !== idx));
  }

  async function handleSubmit() {
    if (!name.trim()) {
      setError("Please enter your name");
      return;
    }
    if (!email.trim()) {
      setError("Please enter your email so we can reply");
      return;
    }
    // Basic email format check — not foolproof but stops the laziest fakes
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())) {
      setError("Please enter a valid email address");
      return;
    }
    if (!message.trim()) {
      setError("Please enter a message");
      return;
    }
    setLoading(true);
    setError("");
    try {
      const API_URL = (process.env.EXPO_PUBLIC_API_URL || "https://dashhound.app") + "/api";
      const res = await fetch(`${API_URL}/feedback`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: name.trim(),
          email: email.trim(),
          message: message.trim(),
          images,
        }),
      });
      if (!res.ok) throw new Error("Failed");
      setSent(true);
      setName("");
      setEmail("");
      setMessage("");
      setImages([]);
    } catch {
      setError("Failed to send. Please try again.");
    } finally {
      setLoading(false);
    }
  }

  return (
    <View style={{ flex: 1, backgroundColor: WHITE }}>
      {Platform.OS === "web" && (
        <Head>
          <title>Support — Dash-Hound Fast CAT Tracker</title>
          <meta name="description" content="Need help with Dash-Hound? Contact us with questions, feedback, or feature requests. Browse FAQs and get the most out of the free Fast CAT tracker app." />
          <meta property="og:title" content="Support — Dash-Hound Fast CAT Tracker" />
          <meta property="og:description" content="Get help with the free Fast CAT tracker app. Contact us with questions, feedback, or feature requests." />
          <meta property="og:url" content="https://dashhound.app/support" />
          <meta name="twitter:title" content="Support — Dash-Hound Fast CAT Tracker" />
          <meta name="twitter:description" content="Get help with the free Fast CAT tracker app. Contact us with questions, feedback, or feature requests." />
        </Head>
      )}
      {/* Web shows the marketing nav; in the native app show a simple in-app
          header with a back button so users don't feel kicked out to the site. */}
      {Platform.OS === "web" ? (
        <PublicNav />
      ) : (
        <View style={{ backgroundColor: NAVY, paddingTop: Platform.OS === "ios" ? 50 : 16 }}>
          <View style={{ paddingVertical: 16, paddingHorizontal: 16, flexDirection: "row", alignItems: "center", justifyContent: "center" }}>
            <TouchableOpacity
              onPress={() => (router.canGoBack() ? router.back() : router.replace("/(tabs)/settings" as any))}
              style={{ position: "absolute", left: 12, top: 0, bottom: 0, justifyContent: "center", paddingHorizontal: 8 }}
              hitSlop={{ top: 12, bottom: 12, left: 12, right: 12 }}
            >
              <Ionicons name="chevron-back" size={26} color="#FFFFFF" />
            </TouchableOpacity>
            <Text style={{ color: "#FFFFFF", fontSize: 18, fontWeight: "800" }}>Support</Text>
          </View>
        </View>
      )}

      <ScrollView contentContainerStyle={styles.content}>
        <View style={styles.hero}>
          <Text style={styles.heroTitle}>Support</Text>
          <Text style={styles.heroSubtitle}>We're here to help</Text>
        </View>

        <View style={styles.body}>
          {/* Clubs callout */}
          <View style={[styles.card, { backgroundColor: NAVY, borderColor: ORANGE }]}>
            <Text style={[styles.cardTitle, { color: ORANGE }]}>📣 Clubs — Send Us Your Stuff!</Text>
            <Text style={[styles.cardText, { color: "#E5E7EB", marginBottom: 6 }]}>
              Hosting a Fast CAT event? We'll feature your listing on Dash-Hound — completely free. Send us:
            </Text>
            <Text style={{ color: "#E5E7EB", fontSize: 14, lineHeight: 24, marginBottom: 4 }}>🎨 Your club logo</Text>
            <Text style={{ color: "#E5E7EB", fontSize: 14, lineHeight: 24, marginBottom: 4 }}>🎗️ Ribbon previews</Text>
            <Text style={{ color: "#E5E7EB", fontSize: 14, lineHeight: 24, marginBottom: 4 }}>📸 Action photos</Text>
            <Text style={{ color: "#E5E7EB", fontSize: 14, lineHeight: 24, marginBottom: 10 }}>📜 Premium list / flyer</Text>
            <Text style={{ color: "#94A3B8", fontSize: 13, lineHeight: 20 }}>
              Just use the form below — attach up to 3 images per message.
            </Text>
          </View>

          {/* Contact Form */}
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Contact Us</Text>
            <Text style={styles.cardText}>
              Have a question, found a bug, or want to suggest a feature? Send us a message below. You can attach up to 3 screenshots or images.
            </Text>

            {sent ? (
              <View style={{ alignItems: "center", paddingVertical: 16 }}>
                <Text style={{ fontSize: 28, marginBottom: 8 }}>✓</Text>
                <Text style={{ color: "#16a34a", fontWeight: "700", fontSize: 16 }}>Thanks for your message!</Text>
                <Text style={{ color: "#6b7280", fontSize: 13, marginTop: 4 }}>We'll get back to you if needed.</Text>
                <TouchableOpacity onPress={() => setSent(false)} style={{ marginTop: 14 }}>
                  <Text style={{ color: ORANGE, fontWeight: "600", fontSize: 14 }}>Send another message</Text>
                </TouchableOpacity>
              </View>
            ) : (
              <>
                {error ? (
                  <View style={{ backgroundColor: "#fef2f2", padding: 10, borderRadius: 8, marginBottom: 12 }}>
                    <Text style={{ color: "#dc2626", textAlign: "center", fontSize: 13 }}>{error}</Text>
                  </View>
                ) : null}
                <TextInput
                  value={name}
                  onChangeText={setName}
                  placeholder="Your name *"
                  placeholderTextColor="#9ca3af"
                  style={styles.input}
                />
                <TextInput
                  value={email}
                  onChangeText={setEmail}
                  placeholder="Your email *"
                  placeholderTextColor="#9ca3af"
                  keyboardType="email-address"
                  autoCapitalize="none"
                  style={styles.input}
                />
                <TextInput
                  value={message}
                  onChangeText={setMessage}
                  placeholder="Your question, suggestion, or feedback..."
                  placeholderTextColor="#9ca3af"
                  multiline
                  numberOfLines={4}
                  style={[styles.input, { minHeight: 120, textAlignVertical: "top" }]}
                />

                {/* Image attachments — web only (HTML file input) */}
                {Platform.OS === "web" && (
                  <View style={{ marginBottom: 10 }}>
                    {images.length > 0 && (
                      <View style={{ flexDirection: "row", flexWrap: "wrap", gap: 8, marginBottom: 10 }}>
                        {images.map((src, i) => (
                          <View key={i} style={{ position: "relative" }}>
                            <Image source={{ uri: src }} style={{ width: 80, height: 80, borderRadius: 8, backgroundColor: "#e5e7eb" }} />
                            <TouchableOpacity
                              onPress={() => removeImage(i)}
                              style={{ position: "absolute", top: -6, right: -6, backgroundColor: "#dc2626", borderRadius: 12, width: 24, height: 24, alignItems: "center", justifyContent: "center" }}
                            >
                              <Text style={{ color: "#fff", fontWeight: "900", fontSize: 14, lineHeight: 16 }}>×</Text>
                            </TouchableOpacity>
                          </View>
                        ))}
                      </View>
                    )}
                    {images.length < 3 && (
                      <TouchableOpacity
                        onPress={handlePickImages}
                        style={{ borderWidth: 1, borderColor: "#d1d5db", borderStyle: "dashed", borderRadius: 10, padding: 12, alignItems: "center" }}
                      >
                        <Text style={{ color: "#374151", fontSize: 14, fontWeight: "600" }}>
                          📎 Attach image{images.length > 0 ? "s" : ""} ({3 - images.length} left)
                        </Text>
                      </TouchableOpacity>
                    )}
                  </View>
                )}

                <TouchableOpacity
                  onPress={handleSubmit}
                  disabled={loading}
                  style={[styles.submitButton, loading && { opacity: 0.6 }]}
                >
                  {loading ? (
                    <ActivityIndicator color="#fff" />
                  ) : (
                    <Text style={styles.submitButtonText}>Send Message</Text>
                  )}
                </TouchableOpacity>
              </>
            )}
          </View>

          {/* FAQ */}
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Frequently Asked Questions</Text>

            <Text style={styles.faqQuestion}>How do I track a run?</Text>
            <Text style={styles.faqAnswer}>
              Go to My Dogs, tap on your dog, then tap "Log a Run". Enter the date, time in seconds, and optionally select Run 1 or Run 2. Speed, points, and title progress are calculated automatically.
            </Text>

            <Text style={styles.faqQuestion}>How do I find events near me?</Text>
            <Text style={styles.faqAnswer}>
              Go to the Events tab and search by state or zip code. Events within 100 miles of your zip code will appear. You can search multiple states by separating them with commas (e.g., NJ, PA, NY).
            </Text>

            <Text style={styles.faqQuestion}>How are breed rankings calculated?</Text>
            <Text style={styles.faqAnswer}>
              Rankings are sourced from the AKC and updated daily. Your dog's projected ranking is based on their best 3 average speed compared to the current year's AKC rankings for their breed. This is not an official AKC ranking.
            </Text>

            <Text style={styles.faqQuestion}>How do I import runs from another app?</Text>
            <Text style={styles.faqAnswer}>
              Go to your dog's profile and tap "Import from Screenshot". Take screenshots of your run history in your other app and upload them. Our AI will automatically read the dates and times and import them.
            </Text>

            <Text style={styles.faqQuestion}>How do I share my dog's profile?</Text>
            <Text style={styles.faqAnswer}>
              On your dog's profile, tap "Make Public" then tap "Share". This creates a shareable link anyone can view without logging in.
            </Text>

            <Text style={styles.faqQuestion}>How do I delete my account?</Text>
            <Text style={styles.faqAnswer}>
              Go to Settings, scroll to the Danger Zone section, and tap "Delete Account". This permanently deletes your account and all associated data including dogs, runs, and photos. This action cannot be undone.
            </Text>
          </View>

          {/* Links */}
          <View style={styles.card}>
            <Text style={styles.cardTitle}>Links</Text>
            <TouchableOpacity onPress={() => router.push("/(auth)/privacy/")}>
              <Text style={styles.link}>Privacy Policy</Text>
            </TouchableOpacity>
            <TouchableOpacity onPress={() => router.push("/(auth)/terms/")}>
              <Text style={styles.link}>Terms of Service</Text>
            </TouchableOpacity>
          </View>
        </View>

        <View style={styles.footer}>
          <Text style={styles.footerText}>© 2026 Dash-Hound LLC. All rights reserved.</Text>
        </View>
      </ScrollView>
    </View>
  );
}

const styles = StyleSheet.create({
  header: {
    backgroundColor: NAVY,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "space-between",
    paddingHorizontal: 20,
    paddingVertical: 14,
    paddingTop: Platform.OS === "ios" ? 50 : 14,
  },
  backButton: { width: 60 },
  backText: { color: "rgba(255,255,255,0.8)", fontSize: 15, fontWeight: "500" },
  headerTitle: { color: WHITE, fontSize: 16, fontWeight: "700" },
  content: { flexGrow: 1 },
  hero: {
    backgroundColor: "#FFF7E6",
    borderBottomWidth: 1,
    borderBottomColor: "#F5DEB3",
    paddingHorizontal: 24,
    paddingBottom: 40,
    paddingTop: 40,
    alignItems: "center",
  },
  heroTitle: { color: NAVY, fontSize: 32, fontWeight: "900" },
  heroSubtitle: { color: "#92400E", fontSize: 15, marginTop: 8 },
  body: {
    maxWidth: 720,
    alignSelf: "center",
    width: "100%",
    paddingHorizontal: 24,
    paddingVertical: 32,
    gap: 20,
  },
  card: {
    backgroundColor: "#f9fafb",
    borderRadius: 14,
    padding: 20,
    borderWidth: 1,
    borderColor: "#e5e7eb",
  },
  cardTitle: { fontSize: 18, fontWeight: "800", color: NAVY, marginBottom: 12 },
  cardText: { fontSize: 15, color: TEXT, lineHeight: 24, marginBottom: 12 },
  input: {
    borderWidth: 1,
    borderColor: "#d1d5db",
    borderRadius: 10,
    padding: 12,
    fontSize: 15,
    color: "#111827",
    marginBottom: 10,
    backgroundColor: "#ffffff",
  },
  submitButton: {
    backgroundColor: NAVY,
    borderRadius: 10,
    padding: 14,
    alignItems: "center",
  },
  submitButtonText: { color: WHITE, fontWeight: "700", fontSize: 16 },
  emailButton: {
    backgroundColor: NAVY,
    borderRadius: 10,
    padding: 14,
    alignItems: "center",
  },
  emailButtonText: { color: WHITE, fontWeight: "700", fontSize: 16 },
  faqQuestion: { fontSize: 15, fontWeight: "700", color: NAVY, marginTop: 16, marginBottom: 6 },
  faqAnswer: { fontSize: 14, color: TEXT, lineHeight: 22 },
  link: { fontSize: 15, color: ORANGE, fontWeight: "600", marginVertical: 8 },
  footer: {
    backgroundColor: NAVY_DARK,
    paddingVertical: 32,
    alignItems: "center",
  },
  footerText: { color: "rgba(255,255,255,0.35)", fontSize: 13 },
});
